import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '@/context/ThemeContext';
import { ArrowRight } from 'lucide-react';
import DiscoveryTool from './DiscoveryTool';

function Particle({ style }) {
  return <div className="absolute rounded-full" style={style} />;
}

export default function HeroSection() {
  const { theme } = useTheme();
  const [showDiscovery, setShowDiscovery] = useState(false);

  const particles = useMemo(() =>
    Array.from({ length: 40 }, (_, i) => ({
      key: i,
      style: {
        width: `${Math.random() * 4 + 1}px`,
        height: `${Math.random() * 4 + 1}px`,
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        backgroundColor: theme === 'dark'
          ? `rgba(0,255,133,${Math.random() * 0.4 + 0.1})`
          : `rgba(13,148,136,${Math.random() * 0.3 + 0.1})`,
        animation: `float-particle ${6 + Math.random() * 8}s ${Math.random() * 4}s infinite ease-in-out`,
      },
    })),
    [theme]
  );

  const words = "Website Anda punya kode, tapi apakah ia punya jiwa?".split(' ');

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden pt-20"
      style={{ backgroundColor: 'var(--bg-primary)' }}
    >
      {/* Particle Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {particles.map(p => <Particle key={p.key} style={p.style} />)}
        {/* Gradient orbs */}
        <div
          className="absolute w-[600px] h-[600px] rounded-full blur-[150px] opacity-20"
          style={{
            background: theme === 'dark' ? '#00FF85' : '#0D9488',
            top: '-10%', right: '-10%',
          }}
        />
        <div
          className="absolute w-[400px] h-[400px] rounded-full blur-[120px] opacity-15"
          style={{
            background: theme === 'dark' ? '#3A82FF' : '#2563EB',
            bottom: '10%', left: '-5%',
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center min-h-[80vh]">
          {/* Left — Text */}
          <div className="space-y-8">
            <motion.div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-mono text-xs tracking-widest uppercase"
              style={{
                background: theme === 'dark' ? 'rgba(0,255,133,0.08)' : 'rgba(13,148,136,0.08)',
                color: 'var(--accent-primary)',
                border: `1px solid ${theme === 'dark' ? 'rgba(0,255,133,0.2)' : 'rgba(13,148,136,0.2)'}`,
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              {theme === 'dark' ? 'Vision Mode' : 'Clarity Mode'}
            </motion.div>

            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tighter leading-[0.95]">
              {words.map((word, i) => (
                <motion.span
                  key={i}
                  className="inline-block mr-[0.3em]"
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + i * 0.08, duration: 0.5 }}
                  style={{
                    color: ['jiwa?'].includes(word) ? 'var(--accent-primary)' : 'var(--text-primary)',
                  }}
                >
                  {word}
                </motion.span>
              ))}
            </h1>

            <motion.p
              className="text-base md:text-lg leading-relaxed max-w-lg"
              style={{ color: 'var(--text-secondary)' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
            >
              {theme === 'dark'
                ? 'Bewhy membangun solusi digital yang dimulai dari tujuan, bukan fitur. Bayangkan website yang bisa berpikir.'
                : 'Bewhy membangun solusi digital yang dimulai dari tujuan, bukan fitur. Data-driven, terukur, dan purposeful.'}
            </motion.p>

            <motion.div
              className="flex flex-wrap gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2 }}
            >
              <button
                onClick={() => setShowDiscovery(true)}
                data-testid="hero-cta-discovery"
                className="group inline-flex items-center gap-2 px-8 py-4 rounded-full font-heading font-semibold text-sm tracking-wide uppercase transition-all duration-300 hover:-translate-y-1"
                style={{
                  backgroundColor: 'var(--accent-primary)',
                  color: theme === 'dark' ? '#0D0D0D' : '#FFFFFF',
                  boxShadow: `0 0 30px ${theme === 'dark' ? 'rgba(0,255,133,0.3)' : 'rgba(13,148,136,0.3)'}`,
                }}
              >
                Digital Vision Check
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </button>
              <a
                href="#portofolio"
                data-testid="hero-cta-portfolio"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-heading font-semibold text-sm tracking-wide uppercase transition-all duration-300 hover:-translate-y-1"
                style={{
                  border: `1px solid var(--border-card)`,
                  color: 'var(--text-primary)',
                }}
              >
                Lihat Karya Kami
              </a>
            </motion.div>
          </div>

          {/* Right — Discovery Tool or Visual */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="relative"
          >
            {showDiscovery ? (
              <DiscoveryTool onClose={() => setShowDiscovery(false)} />
            ) : (
              <div className="glass-card p-8 lg:p-10">
                <div className="space-y-6">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'var(--accent-primary)' }} />
                    <span className="font-mono text-xs tracking-widest uppercase" style={{ color: 'var(--text-secondary)' }}>
                      Digital Vision Health Check
                    </span>
                  </div>
                  <h3 className="font-heading text-xl lg:text-2xl font-semibold" style={{ color: 'var(--text-primary)' }}>
                    Seberapa jernih visi digital bisnis Anda?
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                    5 pertanyaan strategis. 2 menit. Dapatkan skor kejernihan visi digital Anda dan rekomendasi AI yang dipersonalisasi.
                  </p>
                  <button
                    onClick={() => setShowDiscovery(true)}
                    data-testid="hero-start-discovery"
                    className="w-full py-4 rounded-xl font-heading font-semibold text-sm tracking-wide uppercase transition-all duration-300 hover:-translate-y-0.5"
                    style={{
                      background: `linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))`,
                      color: '#FFFFFF',
                    }}
                  >
                    Mulai Assessment Gratis
                  </button>
                  <div className="flex justify-center gap-6 pt-2">
                    {['2 Menit', '5 Pertanyaan', 'Gratis'].map((t) => (
                      <span key={t} className="font-mono text-[10px] tracking-wider uppercase" style={{ color: 'var(--text-secondary)' }}>{t}</span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
